import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  ModalWrapper,
  Header,
  Label,
  Input,
  FileUploadArea,
  FileUploadInput,
  FileInfo,
  FileName,
  FileActions,
  RemoveIcon,
  UploadAreaLabel,
  CheckboxWrapper,
  CheckboxLabel,
  ButtonGroup,
  CancelButton,
  UploadButton,
  ErrorText,
} from "./UploadLetterheadModalStyled";
import { Paperclip } from "@phosphor-icons/react";
import { ErrorIcon } from "../../assets/icons";
import { isLetterheadImageAllowed } from "../../utils/fileValidation";
import DeleteRed from "../../assets/images/DeleteRed.svg";
import UploadIcon from "../../assets/images/Vector.svg";

const LETTERHEAD_ACCEPT = ".png,.jpeg,.jpg";
const LETTERHEAD_FORMATS = "jpeg, png, jpg";

const getExistingFilename = (templateToEdit, type) => {
  if (!templateToEdit) return null;
  // Flat: header_filename, footer_filename
  const flat = templateToEdit?.[`${type}_filename`];
  if (flat) return flat;
  // Alternative: header_image_filename, footer_image_filename
  const alt = templateToEdit?.[`${type}_image_filename`];
  if (alt) return alt;
  // Nested: header_image.filename, footer_image.filename
  const nested = templateToEdit?.[`${type}_image`]?.filename;
  if (nested) return nested;
  // Legacy single-file: file_filename (use for header only)
  if (type === "header" && templateToEdit?.file_filename) return templateToEdit.file_filename;
  return null;
};

const renderFileUploadSection = ({
  type,
  selectedFile,
  templateToEdit,
  isEditMode,
  removedExisting,
  fileInputRef,
  onFileChange,
  onFileDrop,
  onRemoveFile,
  isDragActive,
  onDragEnter,
  onDragLeave,
}) => {
  const filename = getExistingFilename(templateToEdit, type);
  const hasExistingFile = isEditMode && filename && !removedExisting;
  const displayFile = selectedFile || (hasExistingFile ? { name: filename } : null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!displayFile) onDragEnter(type);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) onDragLeave();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragLeave();
    if (!displayFile) {
      const file = e.dataTransfer?.files?.[0];
      if (file) onFileDrop(type, file);
    }
  };

  return (
    <>
      <UploadAreaLabel>
        {type === "header" ? "Header" : "Footer"} Image
      </UploadAreaLabel>
      <FileUploadArea
        onClick={() => !displayFile && fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-has-file={!!displayFile}
        data-is-drag-active={!!displayFile ? false : isDragActive}
        data-testid={`${type}-upload-area`}
      >
        {displayFile ? (
          <FileInfo>
            <Paperclip size={24} weight="regular" style={{ flexShrink: 0 }} />
            <FileName>{displayFile.name}</FileName>
            <FileActions>
              <RemoveIcon
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemoveFile();
                }}
                style={{ cursor: "pointer" }}
              >
                <img src={DeleteRed} alt="Remove" style={{ width: 24, height: 24 }} />
              </RemoveIcon>
            </FileActions>
          </FileInfo>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
            <img src={UploadIcon} alt="Upload" style={{ width: 24, height: 24 }} />
            <div className="upload-placeholder-text">
              Drag a letterhead <strong>{type}</strong> in {LETTERHEAD_FORMATS} formats here or{" "}
              <span className="upload-link" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                click to upload
              </span>
            </div>
          </div>
        )}
        <FileUploadInput
          ref={fileInputRef}
          type="file"
          accept={LETTERHEAD_ACCEPT}
          onChange={onFileChange}
          data-testid={`${type}-file-input`}
        />
      </FileUploadArea>
    </>
  );
};

const UploadLetterheadModal = ({
  isOpen,
  onCancel,
  onUpload,
  firmName,
  firmId,
  uploading = false,
  initialData = null,
  isEditMode = false,
  templateToEdit = null,
}) => {
  const [firmLocation, setFirmLocation] = useState(initialData?.firm_location || "");
  const [headerFile, setHeaderFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);
  const [headerRemoved, setHeaderRemoved] = useState(false);
  const [footerRemoved, setFooterRemoved] = useState(false);
  const [dragActiveType, setDragActiveType] = useState(null);
  const [isDefault, setIsDefault] = useState(initialData?.is_default || false);
  const [error, setError] = useState("");
  const headerInputRef = useRef(null);
  const footerInputRef = useRef(null);

  // Update state when modal opens or initialData changes (for edit mode)
  React.useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFirmLocation(initialData.firm_location || "");
        setIsDefault(initialData.is_default || false);
      } else {
        setFirmLocation("");
        setIsDefault(false);
      }
      setHeaderFile(null);
      setFooterFile(null);
      setHeaderRemoved(false);
      setFooterRemoved(false);
      setDragActiveType(null);
      setError("");
      if (headerInputRef.current) headerInputRef.current.value = null;
      if (footerInputRef.current) footerInputRef.current.value = null;
    }
  }, [isOpen]);

  const processFile = (type, file) => {
    if (!file) return false;

    if (!isLetterheadImageAllowed(file)) {
      setError(`Only ${LETTERHEAD_FORMATS} images are allowed for letterhead.`);
      return false;
    }

    if (type === "header") {
      setHeaderFile(file);
    } else {
      setFooterFile(file);
    }
    setError("");
    return true;
  };

  const handleFileChange = (type) => (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(type, file);
    e.target.value = "";
  };

  const handleFileDrop = (type, file) => {
    processFile(type, file);
  };

  const handleRemoveHeader = () => {
    setHeaderFile(null);
    if (isEditMode) setHeaderRemoved(true);
    if (headerInputRef.current) headerInputRef.current.value = null;
  };

  const handleRemoveFooter = () => {
    setFooterFile(null);
    if (isEditMode) setFooterRemoved(true);
    if (footerInputRef.current) footerInputRef.current.value = null;
  };

  const hasHeader = headerFile || (isEditMode && getExistingFilename(templateToEdit, "header") && !headerRemoved);
  const hasFooter = footerFile || (isEditMode && getExistingFilename(templateToEdit, "footer") && !footerRemoved);

  const handleUpload = () => {
    if (!firmLocation.trim()) {
      setError("Firm Location is required.");
      return;
    }

    if (!isEditMode) {
      if (!headerFile) {
        setError("Please upload a header image.");
        return;
      }
      if (!footerFile) {
        setError("Please upload a footer image.");
        return;
      }
    }

    setError("");
    const uploadData = {
      firm_location: firmLocation.trim(),
      is_default: isDefault,
    };

    if (!isEditMode) {
      uploadData.firm = firmId;
      uploadData.header_file = headerFile;
      uploadData.footer_file = footerFile;
    } else {
      if (headerFile) uploadData.header_file = headerFile;
      if (footerFile) uploadData.footer_file = footerFile;
      if (headerRemoved) uploadData.remove_header_image = true;
      if (footerRemoved) uploadData.remove_footer_image = true;
    }

    onUpload(uploadData);
  };

  const handleCancel = () => {
    if (isEditMode && initialData) {
      setFirmLocation(initialData.firm_location || "");
      setIsDefault(initialData.is_default || false);
    } else {
      setFirmLocation("");
      setIsDefault(false);
    }
    setHeaderFile(null);
    setFooterFile(null);
    setHeaderRemoved(false);
    setFooterRemoved(false);
    setDragActiveType(null);
    setError("");
    if (headerInputRef.current) headerInputRef.current.value = null;
    if (footerInputRef.current) footerInputRef.current.value = null;
    onCancel();
  };

  const isUploadDisabled =
    uploading ||
    !firmLocation.trim() ||
    (!isEditMode && (!headerFile || !footerFile)) ||
    (isEditMode && (!hasHeader || !hasFooter));

  return (
    <ModalWrapper
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      centered
      width={750}
      data-testid="upload-letterhead-modal"
    >
      <Header>{isEditMode ? "Edit letterhead" : `Upload letterhead to ${firmName}`}</Header>

      <Label htmlFor="firmLocation">Firm Location</Label>
      <Input
        id="firmLocation"
        type="text"
        placeholder="Firm Location"
        value={firmLocation}
        onChange={(e) => {
          setFirmLocation(e.target.value);
          if (error) setError("");
        }}
        error={Boolean(error && !firmLocation.trim())}
      />

      {renderFileUploadSection({
        type: "header",
        selectedFile: headerFile,
        templateToEdit,
        isEditMode,
        removedExisting: headerRemoved,
        fileInputRef: headerInputRef,
        onFileChange: handleFileChange("header"),
        onFileDrop: handleFileDrop,
        onRemoveFile: handleRemoveHeader,
        isDragActive: dragActiveType === "header",
        onDragEnter: setDragActiveType,
        onDragLeave: () => setDragActiveType(null),
      })}

      {renderFileUploadSection({
        type: "footer",
        selectedFile: footerFile,
        templateToEdit,
        isEditMode,
        removedExisting: footerRemoved,
        fileInputRef: footerInputRef,
        onFileChange: handleFileChange("footer"),
        onFileDrop: handleFileDrop,
        onRemoveFile: handleRemoveFooter,
        isDragActive: dragActiveType === "footer",
        onDragEnter: setDragActiveType,
        onDragLeave: () => setDragActiveType(null),
      })}

      <CheckboxWrapper>
        <input
          type="checkbox"
          id="isDefault"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        <CheckboxLabel htmlFor="isDefault">
          Make this as default letterhead for {firmName}
        </CheckboxLabel>
      </CheckboxWrapper>

      {error && (
        <ErrorText>
          <ErrorIcon />
          {error}
        </ErrorText>
      )}

      <ButtonGroup>
        <CancelButton onClick={handleCancel} disabled={uploading}>
          Cancel
        </CancelButton>
        <UploadButton onClick={handleUpload} disabled={isUploadDisabled}>
          {uploading ? (isEditMode ? "Updating..." : "Uploading...") : (isEditMode ? "Update" : "Upload")}
        </UploadButton>
      </ButtonGroup>
    </ModalWrapper>
  );
};

UploadLetterheadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  firmName: PropTypes.string.isRequired,
  firmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  uploading: PropTypes.bool,
  initialData: PropTypes.shape({
    firm_location: PropTypes.string,
    is_default: PropTypes.bool,
  }),
  isEditMode: PropTypes.bool,
  templateToEdit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    header_filename: PropTypes.string,
    footer_filename: PropTypes.string,
    header_image: PropTypes.shape({ filename: PropTypes.string, name: PropTypes.string }),
    footer_image: PropTypes.shape({ filename: PropTypes.string, name: PropTypes.string }),
    file_filename: PropTypes.string, // Legacy: single file for backward compatibility
  }),
};

export default UploadLetterheadModal;
